import { useState, useEffect, useCallback, type ReactNode } from 'react';
import { formatDate } from '../lib/dates';
import { api, ApiError, asList } from '../lib/api';
import { formatPaise } from '../lib/money';
import { DataTable, type Column } from '../components/DataTable';

/* ============================================================================
   THE LIBRARY

   The distinction that makes this real: a BOOK is a title, a COPY is a physical
   object on a shelf. You borrow a copy. "Available: 2 of 3" is only meaningful
   because copies exist as their own things.
   ========================================================================== */

interface Copy { id: string; accessionNumber: string; status: string; note: string | null; }
interface Book {
  id: string; title: string; author: string | null; isbn: string | null;
  publisher: string | null; publishedYear: number | null;
  category: string | null; shelfLocation: string | null; description: string | null;
  totalCopies: number; availableCount: number; copies: Copy[];
}
interface Loan {
  id: string; bookId: string; bookTitle: string; bookCopyId: string; accessionNumber: string;
  borrowerUserId: string; borrowerName: string;
  issuedOn: string; dueOn: string; returnedOn: string | null;
  status: string; renewalCount: number; overdueDays: number;
  finePaise: number; fineInvoiceId: string | null; isOverdue: boolean;
}
interface Borrower { userId: string; displayName: string; role: string; studentId: string | null; }
interface BorrowerStatus {
  userId: string; displayName: string; role: string;
  booksOut: number; maxBooks: number; overdueCount: number;
  unpaidFinesPaise: number; canBorrow: boolean; blockedReason: string | null;
}

type Tab = 'catalogue' | 'desk' | 'overdue';

export function LibraryPage() {
  const [tab, setTab] = useState<Tab>('catalogue');

  return (
    <div>
      <div className="page-head">
        <div>
          <div className="eyebrow" style={{ marginBottom: 6 }}>Library</div>
          <h1 className="h-display" style={{ fontSize: 28 }}>Library</h1>
        </div>
      </div>

      <div className="tab-strip" style={{ gap: 4, marginBottom: 18 }}>
        <TabBtn on={tab === 'catalogue'} onClick={() => setTab('catalogue')}>Catalogue</TabBtn>
        <TabBtn on={tab === 'desk'} onClick={() => setTab('desk')}>Issue &amp; return</TabBtn>
        <TabBtn on={tab === 'overdue'} onClick={() => setTab('overdue')}>Overdue</TabBtn>
      </div>

      {tab === 'catalogue' && <Catalogue />}
      {tab === 'desk' && <Desk />}
      {tab === 'overdue' && <Overdue />}
    </div>
  );
}

/* ---------------------------------------------------------------- catalogue */

function Catalogue() {
  const [rows, setRows] = useState<Book[] | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(25);
  const [query, setQuery] = useState('');
  const [adding, setAdding] = useState(false);
  const [active, setActive] = useState<Book | null>(null);

  const load = useCallback(() => {
    setRows(null);
    // ⚠ The endpoint now returns { items, total } rather than a bare array. Before this, the page
    // fetched EVERY book — fine at 30 titles, and a slow page that only gets slower as a library
    // grows, which is the one direction a library only ever goes.
    const params = new URLSearchParams({ skip: String((page - 1) * size), take: String(size) });
    if (query.trim()) params.set('search', query.trim());

    api.get<{ items: Book[]; total: number }>(`/api/library/books?${params}`)
      .then((r) => { setRows(r.items); setTotal(r.total); })
      .catch(() => { setRows([]); setTotal(0); });
  }, [page, size, query]);
  useEffect(load, [load]);

  // A new search resets to page one — otherwise searching from page 3 shows an empty result and
  // reads as "nothing found".
  useEffect(() => { setPage(1); }, [query]);

  const columns: Column<Book>[] = [
    {
      id: 'title', header: 'Title', primary: true, sortable: true,
      value: (b) => b.title, cell: (b) => b.title,
    },
    {
      id: 'author', header: 'Author', sortable: true,
      value: (b) => b.author ?? '', cell: (b) => b.author ?? '—',
    },
    {
      id: 'category', header: 'Category', hideMd: true,
      value: (b) => b.category ?? '',
      cell: (b) => b.category ? <span className="pill pill-muted" style={{ fontSize: 11 }}>{b.category}</span> : '—',
    },
    {
      id: 'shelf', header: 'Shelf', hideSm: true,
      value: (b) => b.shelfLocation ?? '',
      cell: (b) => <span className="mono" style={{ fontSize: 12 }}>{b.shelfLocation ?? '—'}</span>,
    },
    {
      id: 'available', header: 'Available', align: 'right', sortable: true,
      value: (b) => b.availableCount,
      cell: (b) => (
        <span style={{ color: b.availableCount === 0 ? 'var(--ink-3)' : undefined }}>
          <strong>{b.availableCount}</strong> of {b.totalCopies}
        </span>
      ),
    },
    {
      id: 'open', header: '', actions: true,
      cell: (b) => (
        <button className="btn" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => setActive(b)}>
          Copies
        </button>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', marginBottom: 12 }}>
        <button className="btn btn-accent" style={{ marginLeft: 'auto' }} onClick={() => setAdding(true)}>
          + Add book
        </button>
      </div>

      <DataTable
        rows={rows}
        total={total}
        page={page}
        pageSize={size}
        onPage={(p, sz) => { setPage(p); setSize(sz); }}
        onSearch={setQuery}
        columns={columns}
        rowKey={(b) => b.id}
        search
        searchPlaceholder="Search by title, author or ISBN…"
        onRowClick={(b) => setActive(b)}
        empty={{
          title: 'No books yet',
          body: 'Add your first title — you can add several copies of it at once.',
          action: <button className="btn btn-accent" onClick={() => setAdding(true)}>+ Add book</button>,
        }}
      />

      {adding && <AddBookDialog onClose={() => setAdding(false)} onDone={() => { setAdding(false); load(); }} />}
      {active && <CopiesDialog book={active} onClose={() => setActive(null)} onChanged={() => { setActive(null); load(); }} />}
    </div>
  );
}

function AddBookDialog({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [f, setF] = useState({
    title: '', author: '', isbn: '', publisher: '', publishedYear: '',
    category: '', shelfLocation: '', description: '', copies: 1, accessionPrefix: 'ACC',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    if (!f.title.trim()) return;
    setBusy(true); setError(null);
    try {
      await api.post('/api/library/books', {
        ...f,
        publishedYear: f.publishedYear ? Number(f.publishedYear) : null,
        copies: Math.max(1, Number(f.copies)),
      });
      onDone();
    } catch (e) { setError(e instanceof ApiError ? e.message : 'Could not save.'); }
    finally { setBusy(false); }
  };

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <h2 className="h-display" style={{ fontSize: 20, marginBottom: 14 }}>Add a book</h2>
        {error && <div className="pill pill-danger" style={{ padding: '8px 12px', borderRadius: 8, marginBottom: 12, justifyContent: 'flex-start' }}>{error}</div>}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div><label className="label">Title</label>
            <input className="input" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></div>
          <div className="form-row">
            <div><label className="label">Author</label>
              <input className="input" value={f.author} onChange={(e) => setF({ ...f, author: e.target.value })} /></div>
            <div><label className="label">ISBN</label>
              <input className="input mono" value={f.isbn} onChange={(e) => setF({ ...f, isbn: e.target.value })} /></div>
          </div>
          <div className="form-row">
            <div><label className="label">Category</label>
              <input className="input" placeholder="Fiction / Science / Kannada"
                value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} /></div>
            <div><label className="label">Shelf</label>
              <input className="input" placeholder="Rack 3, Shelf B"
                value={f.shelfLocation} onChange={(e) => setF({ ...f, shelfLocation: e.target.value })} /></div>
          </div>

          <div className="card" style={{ padding: 12, background: 'var(--surface-2)' }}>
            <div className="form-row">
              <div><label className="label">How many copies?</label>
                <input className="input" type="number" min={1} value={f.copies}
                  onChange={(e) => setF({ ...f, copies: Number(e.target.value) })} /></div>
              <div><label className="label">Accession prefix</label>
                <input className="input mono" value={f.accessionPrefix}
                  onChange={(e) => setF({ ...f, accessionPrefix: e.target.value })} /></div>
            </div>
            <p style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 6 }}>
              Each physical copy gets its own accession number — the sticker on the spine. That's how
              you identify <em>which</em> copy is out, or lost.
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-accent" disabled={busy || !f.title.trim()} onClick={save} style={{ marginLeft: 'auto' }}>
            {busy ? 'Saving…' : `Add with ${Math.max(1, f.copies)} cop${f.copies === 1 ? 'y' : 'ies'}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function CopiesDialog({ book, onClose, onChanged }: { book: Book; onClose: () => void; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true); setError(null);
    try { await fn(); onChanged(); }
    catch (e) { setError(e instanceof ApiError ? e.message : 'That didn’t work.'); }
    finally { setBusy(false); }
  };

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <h2 className="h-display" style={{ fontSize: 19, marginBottom: 2 }}>{book.title}</h2>
        <p style={{ color: 'var(--ink-3)', fontSize: 13, marginBottom: 16 }}>
          {book.availableCount} of {book.totalCopies} on the shelf
        </p>

        {error && <div className="pill pill-danger" style={{ padding: '8px 12px', borderRadius: 8, marginBottom: 12, justifyContent: 'flex-start' }}>{error}</div>}

        <div style={{ marginBottom: 16 }}>
          {book.copies.map((c) => (
            <div key={c.id} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '9px 0', borderBottom: '1px solid var(--line-2)' }}>
              <span className="mono" style={{ fontSize: 13 }}>{c.accessionNumber}</span>
              <span className={`pill ${c.status === 'Available' ? 'pill-ok' : c.status === 'OnLoan' ? 'pill-warn' : 'pill-danger'}`}
                style={{ fontSize: 11 }}>
                {c.status === 'OnLoan' ? 'On loan' : c.status}
              </span>
              {c.status === 'Available' && (
                <button className="btn" style={{ marginLeft: 'auto', padding: '3px 9px', fontSize: 11 }}
                  disabled={busy}
                  onClick={() => {
                    const note = window.prompt('Write off this copy — why? (damaged / lost from shelf)');
                    if (note === null) return;
                    void act(() => api.post(`/api/library/copies/${c.id}/write-off`, { status: 'Damaged', note }));
                  }}>
                  Write off
                </button>
              )}
            </div>
          ))}
        </div>

        <button className="btn" disabled={busy} onClick={() => {
          const n = window.prompt('How many more copies?', '1');
          if (!n) return;
          void act(() => api.post(`/api/library/books/${book.id}/copies`, { count: Number(n), accessionPrefix: 'ACC' }));
        }}>+ Add copies</button>
      </div>
    </div>
  );
}

/* --------------------------------------------------------------------- desk */

function Desk() {
  return (
    <div className="grid-auto">
      <IssuePanel />
      <ReturnPanel />
    </div>
  );
}

function IssuePanel() {
  const [query, setQuery] = useState('');
  const [borrowers, setBorrowers] = useState<Borrower[]>([]);
  const [picked, setPicked] = useState<Borrower | null>(null);
  const [status, setStatus] = useState<BorrowerStatus | null>(null);
  const [books, setBooks] = useState<Book[]>([]);
  const [bookId, setBookId] = useState('');
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (query.trim().length < 2) { setBorrowers([]); return; }
    const t = setTimeout(() => {
      api.get<Borrower[]>(`/api/library/borrowers?search=${encodeURIComponent(query)}`)
        .then((r) => setBorrowers(asList(r))).catch(() => setBorrowers([]));
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  // The borrower check — shown BEFORE issuing, so a refusal is explained at the desk
  // rather than appearing as a mysterious error after the fact.
  useEffect(() => {
    if (!picked) { setStatus(null); return; }
    api.get<BorrowerStatus>(`/api/library/borrowers/${picked.userId}/status`)
      .then(setStatus).catch(() => setStatus(null));
    // ⚠ The books endpoint now returns { items, total }, not a bare array.
    //
    // This fills a SELECT of available titles, so it needs the whole catalogue rather than a page —
    // a dropdown that silently stops at 25 books is worse than a slow one. take=500 is a ceiling, not
    // a page size; a library past that wants a search box here rather than a longer list.
    api.get<{ items: Book[]; total: number }>('/api/library/books?take=500')
      .then((r) => setBooks(r.items))
      .catch(() => setBooks([]));
  }, [picked]);

  const issue = async () => {
    if (!picked || !bookId) return;
    setBusy(true); setMsg(null);
    try {
      const loan = await api.post<Loan>('/api/library/issue', { bookId, borrowerUserId: picked.userId });
      setMsg({ ok: true, text: `Issued ${loan.accessionNumber} — due ${formatDate(loan.dueOn)}` });
      setBookId('');
      const s = await api.get<BorrowerStatus>(`/api/library/borrowers/${picked.userId}/status`);
      setStatus(s);
    } catch (e) {
      setMsg({ ok: false, text: e instanceof ApiError ? e.message : 'Could not issue.' });
    } finally { setBusy(false); }
  };

  return (
    <div className="card" style={{ padding: 18 }}>
      <h3 style={{ fontSize: 16, fontFamily: 'var(--font-display)', marginBottom: 12 }}>Issue a book</h3>

      <label className="label">Who's borrowing?</label>
      <input className="input" value={picked ? picked.displayName : query}
        placeholder="Search by name…"
        onChange={(e) => { setPicked(null); setQuery(e.target.value); setMsg(null); }} />

      {!picked && borrowers.length > 0 && (
        <div className="card" style={{ marginTop: 4, padding: 0, overflow: 'hidden' }}>
          {borrowers.map((b) => (
            <button key={b.userId} onClick={() => { setPicked(b); setQuery(''); }}
              style={{
                display: 'flex', gap: 8, width: '100%', padding: '9px 12px', textAlign: 'left',
                background: 'none', border: 'none', borderBottom: '1px solid var(--line-2)', cursor: 'pointer',
              }}>
              <span>{b.displayName}</span>
              <span className="pill pill-muted" style={{ fontSize: 10, marginLeft: 'auto' }}>{b.role}</span>
            </button>
          ))}
        </div>
      )}

      {status && (
        <div className="card" style={{
          padding: 12, marginTop: 12,
          background: status.canBorrow ? 'var(--surface-2)' : undefined,
          borderColor: status.canBorrow ? undefined : 'var(--brand)',
        }}>
          <div style={{ fontSize: 13 }}>
            <strong>{status.displayName}</strong> · {status.role}
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--ink-2)', marginTop: 4 }}>
            {status.booksOut} of {status.maxBooks} books out
            {status.overdueCount > 0 && <> · <span style={{ color: 'var(--brand)' }}>{status.overdueCount} overdue</span></>}
            {status.unpaidFinesPaise > 0 && <> · {formatPaise(status.unpaidFinesPaise)} in fines</>}
          </div>
          {!status.canBorrow && (
            <div style={{ fontSize: 13, color: 'var(--brand)', marginTop: 6, fontWeight: 600 }}>
              ⚠ {status.blockedReason}
            </div>
          )}
        </div>
      )}

      {status?.canBorrow && (
        <>
          <label className="label" style={{ marginTop: 12 }}>Which book?</label>
          <select className="input" value={bookId} onChange={(e) => setBookId(e.target.value)}>
            <option value="">Select…</option>
            {books.filter((b) => b.availableCount > 0).map((b) => (
              <option key={b.id} value={b.id}>{b.title} ({b.availableCount} available)</option>
            ))}
          </select>

          <button className="btn btn-accent" style={{ marginTop: 12, width: '100%', justifyContent: 'center' }}
            disabled={busy || !bookId} onClick={issue}>
            {busy ? 'Issuing…' : 'Issue book'}
          </button>
        </>
      )}

      {msg && (
        <div className={`pill ${msg.ok ? 'pill-ok' : 'pill-danger'}`}
          style={{ padding: '9px 12px', borderRadius: 8, marginTop: 12, justifyContent: 'flex-start' }}>
          {msg.text}
        </div>
      )}
    </div>
  );
}

function ReturnPanel() {
  const [accession, setAccession] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<Loan | null>(null);
  const [error, setError] = useState<string | null>(null);

  const doReturn = async () => {
    if (!accession.trim()) return;
    setBusy(true); setError(null); setResult(null);
    try {
      const loan = await api.post<Loan>('/api/library/return', { accessionNumber: accession.trim() });
      setResult(loan);
      setAccession('');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not return.');
    } finally { setBusy(false); }
  };

  return (
    <div className="card" style={{ padding: 18 }}>
      <h3 style={{ fontSize: 16, fontFamily: 'var(--font-display)', marginBottom: 4 }}>Return a book</h3>
      <p style={{ fontSize: 12.5, color: 'var(--ink-3)', marginBottom: 12 }}>
        Type the accession number from the spine.
      </p>

      <div style={{ display: 'flex', gap: 8 }}>
        <input className="input mono" value={accession} placeholder="ACC-12345-01"
          onChange={(e) => setAccession(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') void doReturn(); }}
          style={{ flex: 1 }} autoFocus />
        <button className="btn btn-accent" disabled={busy || !accession.trim()} onClick={doReturn}>
          {busy ? '…' : 'Return'}
        </button>
      </div>

      {error && (
        <div className="pill pill-danger" style={{ padding: '9px 12px', borderRadius: 8, marginTop: 12, justifyContent: 'flex-start' }}>
          {error}
        </div>
      )}

      {result && (
        <div className="card" style={{ padding: 14, marginTop: 12, background: 'var(--surface-2)' }}>
          <strong style={{ fontSize: 14 }}>{result.bookTitle}</strong>
          <div style={{ fontSize: 12.5, color: 'var(--ink-2)', marginTop: 2 }}>
            Returned by {result.borrowerName}
          </div>

          {result.finePaise > 0 ? (
            <div className="pill pill-warn" style={{
              padding: '9px 12px', borderRadius: 8, marginTop: 10,
              display: 'block',
            }}>
              <strong>{result.overdueDays} day(s) overdue — {formatPaise(result.finePaise)} fine</strong>
              <div style={{ fontSize: 12, marginTop: 2 }}>
                {result.fineInvoiceId
                  ? 'Added to their fee account — they can pay it online like any other fee.'
                  : 'No fee account linked, so no invoice was raised.'}
              </div>
            </div>
          ) : (
            <div style={{ fontSize: 13, color: 'var(--ok-ink, #1e7d4f)', marginTop: 8 }}>
              ✓ Returned on time — no fine.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ overdue */

function Overdue() {
  const [rows, setRows] = useState<Loan[] | null>(null);

  const load = useCallback(() => {
    setRows(null);
    api.get<Loan[]>('/api/library/overdue').then(setRows).catch(() => setRows([]));
  }, []);
  useEffect(load, [load]);

  const columns: Column<Loan>[] = [
    {
      id: 'borrower', header: 'Borrower', primary: true, sortable: true,
      value: (l) => l.borrowerName, cell: (l) => l.borrowerName,
    },
    {
      id: 'book', header: 'Book', sortable: true,
      value: (l) => l.bookTitle, cell: (l) => l.bookTitle,
    },
    {
      id: 'accession', header: 'Copy', hideMd: true,
      value: (l) => l.accessionNumber,
      cell: (l) => <span className="mono" style={{ fontSize: 12 }}>{l.accessionNumber}</span>,
    },
    {
      id: 'due', header: 'Was due', sortable: true,
      value: (l) => l.dueOn,
      cell: (l) => formatDate(l.dueOn),
    },
    {
      id: 'late', header: 'Late by', align: 'right', sortable: true,
      value: (l) => {
        const days = Math.floor((Date.now() - new Date(l.dueOn).getTime()) / 86400000);
        return days;
      },
      cell: (l) => {
        const days = Math.floor((Date.now() - new Date(l.dueOn).getTime()) / 86400000);
        return <strong style={{ color: 'var(--brand)' }}>{days} day{days === 1 ? '' : 's'}</strong>;
      },
    },
    {
      id: 'lost', header: '', actions: true,
      cell: (l) => (
        <button className="btn" style={{ padding: '4px 10px', fontSize: 12 }}
          onClick={async () => {
            const cost = window.prompt('Mark as LOST. Replacement cost in rupees:');
            if (!cost) return;
            await api.post(`/api/library/loans/${l.id}/lost`, {
              replacementCostPaise: Math.round(Number(cost) * 100),
            }).catch(() => {});
            load();
          }}>
          Mark lost
        </button>
      ),
    },
  ];

  return (
    <DataTable
      rows={rows}
      columns={columns}
      rowKey={(l) => l.id}
      search
      searchPlaceholder="Search overdue…"
      empty={{ title: 'Nothing overdue', body: 'Every book is back on time. Rare and pleasant.' }}
    />
  );
}

/* -------------------------------------------------------------------------- */

function TabBtn({ on, onClick, children }: { on: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button onClick={onClick} style={{
      background: 'none', border: 'none', cursor: 'pointer', padding: '8px 14px', fontSize: 14,
      color: on ? 'var(--ink)' : 'var(--ink-3)', fontWeight: on ? 600 : 400,
      borderBottom: on ? '2px solid var(--brand)' : '2px solid transparent',
    }}>{children}</button>
  );
}
